import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Page,
  Layout,
  Card,
  DataTable,
  Button,
  TextField,
  Modal,
  FormLayout,
  Select,
  Toast,
  Badge,
  Pagination,
  Filters,
  ChoiceList,
  Banner,
  Divider,
  Text,
  InlineStack,
  BlockStack,
  Box,
  Autocomplete,
  Icon
} from '@shopify/polaris';
import { SearchIcon } from '@shopify/polaris-icons';
import { vendorAPI, templateAPI } from '../utils/api';
import { SHOPIFY_FIELD_OPTIONS } from '../constants/shopifyFieldOptions';
import { FieldSelector } from '../components/FieldSelector';
import { CodeEditor } from '../components/CodeEditor';

function VendorManagement() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState([]);
  const [modalActive, setModalActive] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [templateModalActive, setTemplateModalActive] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [toast, setToast] = useState(null);
  const [formData, setFormData] = useState({
    vendorName: '',
    vendorCode: '',
    contactPerson: '',
    email: '',
    phone: '',
    address: '',
    status: 'active',
    notes: '',
    preferredExportFormat: 'xlsx'
  });
  const [fieldErrors, setFieldErrors] = useState({
    vendorName: '',
    vendorCode: '',
    email: ''
  });

  const fetchVendors = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(searchValue && { search: searchValue }),
        ...(statusFilter.length > 0 && { status: statusFilter[0] })
      };
      const response = await vendorAPI.getAll(params);
      setVendors(response.data.vendors);
      setPagination(prev => ({ ...prev, total: response.data.pagination.total }));
    } catch (error) {
      console.error('Error fetching vendors:', error);
      setToast({ content: 'Error loading vendors', error: true });
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, searchValue, statusFilter]);

  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  const validateForm = () => {
    const errors = {
      vendorName: '',
      vendorCode: '',
      email: ''
    };
    let isValid = true;

    if (!formData.vendorName.trim()) {
      errors.vendorName = 'Vendor name is required';
      isValid = false;
    }

    if (!formData.vendorCode.trim()) {
      errors.vendorCode = 'Vendor code is required';
      isValid = false;
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
      isValid = false;
    }

    setFieldErrors(errors);
    return isValid;
  };

  const handleModalOpen = (vendor = null) => {
    if (vendor) {
      setEditingVendor(vendor);
      setFormData({
        vendorName: vendor.vendorName,
        vendorCode: vendor.vendorCode,
        contactPerson: vendor.contactPerson || '',
        email: vendor.email || '',
        phone: vendor.phone || '',
        address: vendor.address || '',
        status: vendor.status,
        notes: vendor.notes || '',
        preferredExportFormat: vendor.preferredExportFormat
      });
    } else {
      setEditingVendor(null);
      setFormData({
        vendorName: '',
        vendorCode: '',
        contactPerson: '',
        email: '',
        phone: '',
        address: '',
        status: 'active',
        notes: '',
        preferredExportFormat: 'xlsx'
      });
    }
    setFieldErrors({
      vendorName: '',
      vendorCode: '',
      email: ''
    });
    setModalActive(true);
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      if (editingVendor) {
        await vendorAPI.update(editingVendor.id, formData);
        
        // If preferred export format changed, update the active template format too
        if (formData.preferredExportFormat !== editingVendor.preferredExportFormat) {
          try {
            const templatesResponse = await templateAPI.getVendorTemplate(editingVendor.id);
            const templates = templatesResponse.data;
            const activeTemplate = templates.find(t => t.isActive);
            
            if (activeTemplate && activeTemplate.format !== formData.preferredExportFormat) {
              await templateAPI.updateTemplate(
                editingVendor.id,
                activeTemplate.id,
                { format: formData.preferredExportFormat }
              );
            }
          } catch (templateError) {
            console.error('Error updating template format:', templateError);
            // Don't fail vendor update if template update fails
          }
        }
        
        setToast({ content: 'Vendor updated successfully' });
      } else {
        await vendorAPI.create(formData);
        setToast({ content: 'Vendor created successfully' });
      }
      setModalActive(false);
      fetchVendors();
    } catch (error) {
      setToast({ content: error.response?.data?.error || 'Error saving vendor', error: true });
    }
  };

  const handleDelete = async (id) => {
    if (confirm('Are you sure you want to delete this vendor?')) {
      try {
        await vendorAPI.delete(id);
        setToast({ content: 'Vendor deleted successfully' });
        fetchVendors();
      } catch (error) {
        setToast({ content: error.response?.data?.error || 'Error deleting vendor', error: true });
      }
    }
  };

  const handleTemplateClick = (vendor) => {
    setSelectedVendor(vendor);
    setTemplateModalActive(true);
  };

  const rows = vendors.map((vendor) => [
    vendor.vendorName,
    vendor.vendorCode,
    vendor.email || '-',
    vendor.phone || '-',
    <Badge status={vendor.status === 'active' ? 'success' : 'critical'}>
      {vendor.status}
    </Badge>,
    vendor.templates?.length > 0 ? <Badge status="success">Yes</Badge> : <Badge>No</Badge>,
    <div style={{ display: 'flex', gap: '8px' }}>
      <Button size="slim" onClick={() => handleModalOpen(vendor)}>Edit</Button>
      <Button size="slim" onClick={() => handleTemplateClick(vendor)}>Template</Button>
      <Button size="slim" tone="critical" onClick={() => handleDelete(vendor.id)}>Delete</Button>
    </div>
  ]);

  const handleClearFilters = () => {
    setSearchValue('');
    setStatusFilter([]);
  };

  return (
    <Page
      title="Vendor Management"
      primaryAction={{
        content: 'Add Vendor',
        onAction: () => handleModalOpen()
      }}
    >
      <BlockStack gap="400">
        <Card>
            <div style={{ padding: '16px' }}>
              <Filters
                queryValue={searchValue}
                queryPlaceholder="Search vendors..."
                filters={[
                  {
                    key: 'status',
                    label: 'Status',
                    filter: (
                      <ChoiceList
                        title="Status"
                        titleHidden
                        choices={[
                          { label: 'Active', value: 'active' },
                          { label: 'Inactive', value: 'inactive' }
                        ]}
                        selected={statusFilter}
                        onChange={setStatusFilter}
                      />
                    ),
                    shortcut: true
                  }
                ]}
                appliedFilters={[]}
                onQueryChange={setSearchValue}
                onQueryClear={() => setSearchValue('')}
                onClearAll={handleClearFilters}
              />
            </div>

            <DataTable
              columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text']}
              headings={['Vendor Name', 'Code', 'Email', 'Phone', 'Status', 'Has Template', 'Actions']}
              rows={rows}
              loading={loading}
            />

            <div style={{ padding: '16px', display: 'flex', justifyContent: 'center' }}>
              <Pagination
                hasPrevious={pagination.page > 1}
                onPrevious={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                hasNext={pagination.page < Math.ceil(pagination.total / pagination.limit)}
                onNext={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              />
            </div>
          </Card>
        </BlockStack>

        <Modal
          open={modalActive}
          onClose={() => setModalActive(false)}
          title={editingVendor ? 'Edit Vendor' : 'Add Vendor'}
          primaryAction={{
            content: 'Save',
            onAction: handleSubmit
          }}
          secondaryActions={[
            {
              content: 'Cancel',
              onAction: () => setModalActive(false)
            }
          ]}
        >
          <Modal.Section>
            <FormLayout>
              <TextField
                label="Vendor Name"
                value={formData.vendorName}
                onChange={(value) => {
                  setFormData({ ...formData, vendorName: value });
                  if (fieldErrors.vendorName) {
                    setFieldErrors({ ...fieldErrors, vendorName: '' });
                  }
                }}
                error={fieldErrors.vendorName}
                autoComplete="off"
                requiredIndicator
              />
              <TextField
                label="Vendor Code"
                value={formData.vendorCode}
                onChange={(value) => {
                  setFormData({ ...formData, vendorCode: value });
                  if (fieldErrors.vendorCode) {
                    setFieldErrors({ ...fieldErrors, vendorCode: '' });
                  }
                }}
                error={fieldErrors.vendorCode}
                autoComplete="off"
                requiredIndicator
              />
              <TextField
                label="Contact Person"
                value={formData.contactPerson}
                onChange={(value) => setFormData({ ...formData, contactPerson: value })}
                autoComplete="off"
              />
              <TextField
                label="Email"
                type="email"
                value={formData.email}
                onChange={(value) => {
                  setFormData({ ...formData, email: value });
                  if (fieldErrors.email) {
                    setFieldErrors({ ...fieldErrors, email: '' });
                  }
                }}
                error={fieldErrors.email}
                autoComplete="off"
              />
              <TextField
                label="Phone"
                type="tel"
                value={formData.phone}
                onChange={(value) => setFormData({ ...formData, phone: value })}
                autoComplete="off"
              />
              <TextField
                label="Address"
                value={formData.address}
                onChange={(value) => setFormData({ ...formData, address: value })}
                multiline={3}
                autoComplete="off"
              />
              <Select
                label="Status"
                options={[
                  { label: 'Active', value: 'active' },
                  { label: 'Inactive', value: 'inactive' }
                ]}
                value={formData.status}
                onChange={(value) => setFormData({ ...formData, status: value })}
              />
              <Select
                label="Preferred Export Format"
                options={[
                  { label: 'Excel (XLSX)', value: 'xlsx' },
                  { label: 'CSV', value: 'csv' }
                ]}
                value={formData.preferredExportFormat}
                onChange={(value) => setFormData({ ...formData, preferredExportFormat: value })}
              />
              <TextField
                label="Notes"
                value={formData.notes}
                onChange={(value) => setFormData({ ...formData, notes: value })}
                multiline={3}
                autoComplete="off"
              />
            </FormLayout>
          </Modal.Section>
        </Modal>

        {templateModalActive && selectedVendor && (
          <TemplateModal
            vendor={selectedVendor}
            active={templateModalActive}
            onClose={() => {
              setTemplateModalActive(false);
              setSelectedVendor(null);
            }}
            onSuccess={() => {
              setToast({ content: 'Template saved successfully' });
              fetchVendors();
            }}
          />
        )}

        {toast && (
          <Toast
            content={toast.content}
            error={toast.error}
            onDismiss={() => setToast(null)}
          />
        )}
      </Page>
  );
}

const EMPTY_ROW = () => ({ columnName: '', fieldType: 'field', sourceField: 'order.orderNumber', defaultValue: '', snippetCode: '' });

function TemplateModal({ vendor, active, onClose, onSuccess }) {
  const [existingTemplate, setExistingTemplate] = useState(null);
  const [loadingTemplate, setLoadingTemplate] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [templateName, setTemplateName] = useState('');
  const [format, setFormat] = useState('xlsx');
  const [rows, setRows] = useState([EMPTY_ROW()]);
  const [testResults, setTestResults] = useState({});
  const [testLoading, setTestLoading] = useState({});
  const columnRefs = useRef([]);
  const [scrollToIndex, setScrollToIndex] = useState(null);

  useEffect(() => {
    if (!active) return;
    setLoadingTemplate(true);
    setError(null);
    templateAPI.getVendorTemplate(vendor.id)
      .then(response => {
        const templates = response.data;
        if (templates.length > 0) {
          const t = templates.find(t => t.isActive) || templates[0];
          setExistingTemplate(t);
          setTemplateName(t.templateName);
          setFormat(t.format);
          const parsed = JSON.parse(t.mappingJson);
          setRows(parsed.map(m => ({
            columnName: m.columnName,
            fieldType: m.fieldType || 'field',
            sourceField: m.sourceField || 'order.orderNumber',
            defaultValue: m.defaultValue || '',
            snippetCode: m.snippetCode || ''
          })));
        } else {
          setExistingTemplate(null);
          setTemplateName(`${vendor.vendorName} Template`);
          setFormat('xlsx');
          setRows([EMPTY_ROW()]);
        }
      })
      .catch(() => setError('Failed to load template.'))
      .finally(() => setLoadingTemplate(false));
  }, [active, vendor.id, vendor.vendorName]);

  const updateRow = (index, field, value) => {
    setRows(prev => prev.map((r, i) => i === index ? { ...r, [field]: value } : r));
  };

  // Scroll to new column after it's rendered
  useEffect(() => {
    if (scrollToIndex !== null) {
      // Use setTimeout to ensure DOM is fully updated
      const timer = setTimeout(() => {
        const newCard = columnRefs.current[scrollToIndex];
        if (newCard) {
          newCard.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center'
          });
        }
        setScrollToIndex(null);
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [scrollToIndex]);

  const addRow = () => {
    const newIndex = rows.length;
    setRows(prev => [...prev, EMPTY_ROW()]);
    // Set the index to scroll to after render
    setScrollToIndex(newIndex);
  };

  const removeRow = (index) => setRows(prev => prev.filter((_, i) => i !== index));

  const moveRow = (index, direction) => {
    setRows(prev => {
      const next = [...prev];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const handleTestSnippet = async (index, snippetCode) => {
    setTestLoading(prev => ({ ...prev, [index]: true }));
    setTestResults(prev => ({ ...prev, [index]: null }));
    
    try {
      const response = await templateAPI.testSnippet(vendor.id, { snippetCode, sampleSize: 3 });
      setTestResults(prev => ({ ...prev, [index]: response.data }));
    } catch (err) {
      setTestResults(prev => ({ 
        ...prev, 
        [index]: { 
          error: err.response?.data?.error || 'Failed to test snippet',
          details: err.response?.data?.details
        } 
      }));
    } finally {
      setTestLoading(prev => ({ ...prev, [index]: false }));
    }
  };

  const handleSave = async () => {
    setError(null);
    if (!templateName.trim()) { setError('Template name is required.'); return; }
    const emptyLabel = rows.findIndex(r => !r.columnName.trim());
    if (emptyLabel !== -1) { setError(`Row ${emptyLabel + 1}: Column label is required.`); return; }
    const labels = rows.map(r => r.columnName.trim());
    const dup = labels.find((l, i) => labels.indexOf(l) !== i);
    if (dup) { setError(`Duplicate column label: "${dup}"`); return; }

    const mappings = rows.map((r, i) => ({
      columnName: r.columnName.trim(),
      fieldType: r.fieldType || 'field',
      sourceField: r.sourceField,
      snippetCode: r.snippetCode || '',
      fieldOrder: i,
      defaultValue: r.defaultValue || ''
    }));

    setSaving(true);
    try {
      if (existingTemplate) {
        await templateAPI.updateTemplate(vendor.id, existingTemplate.id, { templateName, format, mappings });
      } else {
        await templateAPI.createTemplate(vendor.id, { templateName, format, mappings });
      }
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save template.');
    } finally {
      setSaving(false);
    }
  };

  const fieldLabelMap = Object.fromEntries(
    SHOPIFY_FIELD_OPTIONS.filter(o => o.value).map(o => [o.value, o.label])
  );

  // Add custom style for modal width
  useEffect(() => {
    const styleId = 'template-modal-style';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .Polaris-Modal-Dialog__Modal {
          max-width: 60vw !important;
          width: 60vw !important;
        }
      `;
      document.head.appendChild(style);
    }
    return () => {
      const styleEl = document.getElementById(styleId);
      if (styleEl) styleEl.remove();
    };
  }, []);

  return (
    <Modal
      open={active}
      onClose={onClose}
      title={`${existingTemplate ? 'Edit' : 'Create'} Template — ${vendor.vendorName}`}
      large
      primaryAction={{ content: existingTemplate ? 'Update Template' : 'Create Template', onAction: handleSave, loading: saving }}
      secondaryActions={[{ content: 'Cancel', onAction: onClose }]}
    >
      <Modal.Section>
        {loadingTemplate ? (
          <p>Loading template...</p>
        ) : (
          <BlockStack gap="400">
            {error && <Banner tone="critical">{error}</Banner>}

            <FormLayout>
              <FormLayout.Group>
                <TextField
                  label="Template Name"
                  value={templateName}
                  onChange={setTemplateName}
                  autoComplete="off"
                  requiredIndicator
                />
                <Select
                  label="Export Format"
                  options={[
                    { label: 'Excel (XLSX)', value: 'xlsx' },
                    { label: 'CSV', value: 'csv' }
                  ]}
                  value={format}
                  onChange={setFormat}
                />
              </FormLayout.Group>
            </FormLayout>

            <Divider />

            <BlockStack gap="200">
              <InlineStack align="space-between" blockAlign="center">
                <Text variant="headingSm">Column Mappings</Text>
              </InlineStack>

              <BlockStack gap="400">
                {rows.map((row, index) => (
                  <div key={index} ref={el => columnRefs.current[index] = el}>
                  <Card>
                    <BlockStack gap="400">
                      {/* Header with column number and actions */}
                      <InlineStack align="space-between" blockAlign="center">
                        <Text variant="headingMd" as="h3">Column {index + 1}</Text>
                        <InlineStack gap="200">
                          <Button size="slim" disabled={index === 0} onClick={() => moveRow(index, -1)}>↑</Button>
                          <Button size="slim" disabled={index === rows.length - 1} onClick={() => moveRow(index, 1)}>↓</Button>
                          <Button
                            size="slim"
                            tone="critical"
                            disabled={rows.length === 1}
                            onClick={() => removeRow(index)}
                          >
                            Remove
                          </Button>
                        </InlineStack>
                      </InlineStack>

                      {/* Compact two-column layout for basic fields */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                        <TextField
                          label="Column Label"
                          value={row.columnName}
                          onChange={(v) => updateRow(index, 'columnName', v)}
                          placeholder="e.g. Order No."
                          autoComplete="off"
                          requiredIndicator
                        />
                        <Select
                          label="Field Type"
                          options={[
                            { label: 'Shopify Field', value: 'field' },
                            { label: 'Code Snippet', value: 'snippet' }
                          ]}
                          value={row.fieldType || 'field'}
                          onChange={(v) => updateRow(index, 'fieldType', v)}
                        />
                      </div>

                      {/* Field-specific content */}
                      {row.fieldType === 'snippet' ? (
                        <BlockStack gap="300">
                          <CodeEditor
                            value={row.snippetCode}
                            onChange={(v) => updateRow(index, 'snippetCode', v)}
                            onTest={(code) => handleTestSnippet(index, code)}
                            testResults={testResults[index]}
                            testLoading={testLoading[index]}
                          />
                          <TextField
                            label="Default / Fallback Value"
                            value={row.defaultValue}
                            onChange={(v) => updateRow(index, 'defaultValue', v)}
                            placeholder="(optional - used if snippet fails)"
                            autoComplete="off"
                          />
                        </BlockStack>
                      ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px' }}>
                          <div>
                            <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 600, color: '#202223' }}>
                              Shopify Source Field <span style={{ color: '#d72c0d' }}>*</span>
                            </label>
                            <FieldSelector
                              options={SHOPIFY_FIELD_OPTIONS}
                              value={row.sourceField}
                              onChange={(v) => updateRow(index, 'sourceField', v)}
                              placeholder="Search fields..."
                            />
                          </div>
                          <TextField
                            label="Default / Fallback Value"
                            value={row.defaultValue}
                            onChange={(v) => updateRow(index, 'defaultValue', v)}
                            placeholder="(optional)"
                            autoComplete="off"
                          />
                        </div>
                      )}
                    </BlockStack>
                  </Card>
                  </div>
                ))}
              </BlockStack>

              {rows.length === 0 && (
                <Box padding="400" background="bg-surface-secondary" borderRadius="200">
                  <Text alignment="center" tone="subdued">No columns added yet. Click "+ Add Column" to start.</Text>
                </Box>
              )}

              {/* Floating Add Button - sticks to bottom when scrolling */}
              <div style={{
                position: 'sticky',
                bottom: '0px',
                display: 'flex',
                justifyContent: 'flex-end',
                paddingTop: '16px',
                paddingBottom: '16px',
                backgroundColor: 'white',
                zIndex: 100
              }}>
                <Button
                  onClick={addRow}
                  size="large"
                  variant="primary"
                >
                  + Add Column
                </Button>
              </div>

              <Text tone="subdued" variant="bodySm">
                Each column becomes a header in the exported file. Choose a Shopify field to map the data, or select "Static / Empty Value" to output a fixed value (enter it in the Default / Static Value column). The default value is used as a fallback when the Shopify field is empty.
              </Text>
            </BlockStack>
          </BlockStack>
        )}
      </Modal.Section>
    </Modal>
  );
}

export default VendorManagement;
